/* Daily booking — the per-person ACTUALS view, paired with the weekly
 * staffing plan in one container (StaffingBookingPanel).
 *
 *   ┌─ Staffing & booking ─────────────────────────────────────┐
 *   │  weekly staffing overview (click a CW to open it below)   │
 *   │                    ▼  (funnel)  CW21 · Mon 19 – Fri 23    │
 *   │  Project A   4h     4h     4h     4h     4h               │
 *   │  Internal    2h     2h     2h     2h     2h               │
 *   │  Free cap.   2h     2h     2h     2h     2h               │
 *   │  Total       8h     8h     8h     8h     8h               │
 *   └───────────────────────────────────────────────────────────┘
 *
 * Editing matches the staffing grid: add a line (relevance-ranked
 * picker, not the full catalogue), click a cell to type hours, drag a
 * cell across days to copy. Each change saves immediately. Cells
 * pre-filled from the plan but not yet booked render faint; booked
 * actuals render solid.
 */
import React from 'react';
import {
  listBookings, upsertBooking, getUserStaffing, listProjects, listTaxonomies,
  listStaffingCategories,
} from '../api.js';
import { StaffingEmbed } from './staffing.jsx';
import { EntityLink } from './primitives.jsx';
import { useAnchoredPosition, HIDDEN_ANCHORED_STYLE } from '../lib/use-anchored-position.jsx';

/* ── date helpers (UTC) ───────────────────────────────────────── */
const DAY_MS = 86_400_000;
const round1 = n => Math.round((Number(n) || 0) * 10) / 10;

function mondayOf(date) {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const wd = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() - (wd - 1));
  return d;
}
function addDays(date, n) { const d = new Date(date); d.setUTCDate(d.getUTCDate() + n); return d; }
function ymd(date) { return date.toISOString().slice(0, 10); }
function isoWeekString(date) {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const isoYear = d.getUTCFullYear();
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const week = 1 + Math.round(((d.getTime() - jan4.getTime()) / DAY_MS - 3 + ((jan4.getUTCDay() || 7) - 1)) / 7);
  return `${isoYear}-W${String(week).padStart(2, '0')}`;
}
function mondayOfIsoWeek(isoWeek) {
  const m = /^(\d{4})-W(\d{2})$/.exec(isoWeek || '');
  if (!m) return mondayOf(new Date());
  const year = +m[1], week = +m[2];
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Dow = jan4.getUTCDay() || 7;
  const week1MonMs = Date.UTC(year, 0, 4 - (jan4Dow - 1));
  return new Date(week1MonMs + (week - 1) * 7 * DAY_MS);
}
const currentIsoWeek = () => isoWeekString(new Date());

const WORKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

/* Categories are admin-managed (Admin → Categories) and shared with the
 * staffing plan. This fallback mirrors the seed so the grid renders if the
 * fetch hasn't resolved (or the endpoint 404s pre-migration). */
const FALLBACK_CATEGORIES = [
  { slug: 'sales',          label: 'Sales',          color: '#2E7DAF', clientLinked: true  },
  { slug: 'internal',       label: 'Internal',       color: '#715AAE', clientLinked: false },
  { slug: 'recruiting',     label: 'Recruiting',     color: '#C98A2B', clientLinked: false },
  { slug: 'vacation',       label: 'Vacation',       color: '#3CA88A', clientLinked: false },
  { slug: 'public-holiday', label: 'Public holiday', color: '#A0A0A0', clientLinked: false },
  { slug: 'sick',           label: 'Sick',           color: '#C25450', clientLinked: false },
];

/* Row key mirrors the server (functions/_lib/staffing.ts → bookingRowKey):
 *   client-project          → 'c:<projectId>'
 *   client-linked (+client) → 's:<itemType>:<clientId>'
 *   everything else         → 't:<itemType>'. */
function rowKeyOf(itemType, projectId, clientId) {
  if (itemType === 'client-project') return `c:${projectId || ''}`;
  if (clientId) return `s:${itemType}:${clientId}`;
  return `t:${itemType}`;
}

/* ══ Combined panel: staffing overview + funnel + booking ══════ */
export const StaffingBookingPanel = ({ userId, canEdit = false }) => {
  const [selectedWeek, setSelectedWeek] = React.useState(currentIsoWeek);
  const wkStart = React.useMemo(() => mondayOfIsoWeek(selectedWeek), [selectedWeek]);
  const range = `${WORKDAY_LABELS[0]} ${addDays(wkStart, 0).getUTCDate()} – ${WORKDAY_LABELS[4]} ${addDays(wkStart, 4).getUTCDate()}`;

  return (
    <div style={{background:'var(--bg)'}}>
      <div style={{padding:'10px 0 0', fontSize:11, letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--fg-muted)', fontWeight:600}}>
        Staffing plan · click a calendar week to book it
      </div>
      <StaffingEmbed userIds={[userId]} weeks={16} bare selectedWeek={selectedWeek} onSelectWeek={setSelectedWeek}/>

      {/* Funnel ("Trichter") linking the selected week to the table */}
      <div style={{display:'flex', flexDirection:'column', alignItems:'center', position:'relative'}}>
        <div style={{
          marginTop:6, padding:'3px 12px', borderRadius:14, fontSize:11.5, fontWeight:600,
          background:'var(--orange)', color:'#fff', whiteSpace:'nowrap', zIndex:1,
        }}>{selectedWeek} · {range}</div>
        <div style={{
          width:'100%', height:18, marginTop:-2,
          background:'rgba(255,100,40,0.16)',
          clipPath:'polygon(44% 0, 56% 0, 100% 100%, 0 100%)',
        }}/>
      </div>

      <div style={{padding:'0 14px 14px'}}>
        <DailyBookingGrid userId={userId} canEdit={canEdit} isoWeek={selectedWeek} onWeekChange={setSelectedWeek}/>
      </div>
    </div>
  );
};

/* ══ Daily booking grid (week controllable from the panel) ═════ */
export const DailyBookingGrid = ({ userId, canEdit = false, isoWeek: controlledWeek = null, onWeekChange = null }) => {
  const [internalWeek, setInternalWeek] = React.useState(currentIsoWeek);
  const isoWeek = controlledWeek || internalWeek;
  const setIsoWeek = wk => { if (onWeekChange) onWeekChange(wk); else setInternalWeek(wk); };
  const weekStart = React.useMemo(() => mondayOfIsoWeek(isoWeek), [isoWeek]);
  const days = React.useMemo(
    () => WORKDAY_LABELS.map((label, i) => {
      const d = addDays(weekStart, i);
      return { date: ymd(d), label, dayNum: d.getUTCDate(), isToday: ymd(d) === ymd(new Date()) };
    }),
    [weekStart],
  );

  const [bookings, setBookings]   = React.useState([]);
  const [allPlan, setAllPlan]     = React.useState([]);   // every staffing entry (all weeks)
  const [weeklyHours, setWeekly]  = React.useState(40);
  const [maxHours, setMaxHours]   = React.useState(8);
  const [projects, setProjects]   = React.useState([]);
  const [clients,  setClients]    = React.useState([]);
  const [categories, setCategories] = React.useState(FALLBACK_CATEGORIES);
  const [extraRows, setExtraRows] = React.useState([]);
  const [serverEdit, setServerEdit] = React.useState(false);
  const [error, setError]   = React.useState(null);
  const [status, setStatus] = React.useState(null);     // 'saving' | 'saved'
  const [addOpen, setAddOpen] = React.useState(false);
  const [edit, setEdit] = React.useState(null);          // { rowKey, date }
  const [draft, setDraft] = React.useState('');

  // Drag-to-fill across days within one row.
  const [drag, setDrag] = React.useState(null);          // { rowKey, startIdx, endIdx, value, moved }
  const dragRef = React.useRef(null); dragRef.current = drag;

  React.useEffect(() => {
    listProjects().then(setProjects).catch(() => setProjects([]));
    listTaxonomies().then(t => setClients(t.clients || [])).catch(() => setClients([]));
    listStaffingCategories().then(cs => setCategories(cs && cs.length ? cs : FALLBACK_CATEGORIES)).catch(() => setCategories(FALLBACK_CATEGORIES));
  }, []);

  // Category-derived lookups shared by the rows, labels, dots and picker.
  const catBySlug       = React.useMemo(() => { const m = new Map(); for (const c of categories) m.set(c.slug, c); return m; }, [categories]);
  const clientLinkedSet = React.useMemo(() => new Set(categories.filter(c => c.clientLinked).map(c => c.slug)), [categories]);
  const itemLabel = React.useCallback(slug => slug === 'client-project' ? 'Client project' : (catBySlug.get(slug)?.label || slug), [catBySlug]);
  const typeColor = React.useCallback(slug => slug === 'client-project' ? 'var(--orange)' : (catBySlug.get(slug)?.color || 'var(--fg-muted)'), [catBySlug]);
  const rankOf    = React.useCallback(slug => { if (slug === 'client-project') return -1; const i = categories.findIndex(c => c.slug === slug); return i < 0 ? 99 : i; }, [categories]);

  const fromDate = days[0].date, toDate = days[days.length - 1].date;
  const reload = React.useCallback(() => {
    setError(null);
    Promise.all([
      listBookings({ userId, from: fromDate, to: toDate }).catch(() => ({ bookings: [], weeklyHours: 40, maxHoursPerDay: 8, canEdit: false })),
      getUserStaffing(userId).then(s => s.entries || []).catch(() => []),
    ]).then(([bk, entries]) => {
      setBookings(bk.bookings || []);
      setWeekly(bk.weeklyHours || 40);
      setMaxHours(bk.maxHoursPerDay || 8);
      setServerEdit(!!bk.canEdit);
      setAllPlan(entries);
      setExtraRows([]);
    }).catch(err => setError(err.message || String(err)));
  }, [userId, fromDate, toDate]);
  React.useEffect(reload, [reload]);

  const editable = canEdit && serverEdit;

  const clientsById = React.useMemo(() => { const m = new Map(); for (const c of clients) m.set(c.id || c._id, c); return m; }, [clients]);
  const projectsById = React.useMemo(() => { const m = new Map(); for (const p of projects) m.set(p.id || p._id, p); return m; }, [projects]);

  const planEntries = React.useMemo(
    () => allPlan.filter(e => e.isoWeek === isoWeek && (e.percentage || 0) > 0),
    [allPlan, isoWeek],
  );

  // "Your projects" for the picker — staffed or booked at any point.
  const relevantProjectIds = React.useMemo(() => {
    const s = new Set();
    for (const e of allPlan) if (e.projectId) s.add(e.projectId);
    for (const b of bookings) if (b.projectId) s.add(b.projectId);
    return s;
  }, [allPlan, bookings]);
  const relevantClientIds = React.useMemo(() => {
    const s = new Set();
    for (const id of relevantProjectIds) { const p = projectsById.get(id); if (p?.clientId) s.add(p.clientId); }
    for (const b of bookings) if (b.clientId) s.add(b.clientId);
    return s;
  }, [relevantProjectIds, projectsById, bookings]);

  const basisByKey = React.useMemo(() => {
    const m = new Map();
    for (const e of planEntries) {
      // A client-linked plan row (e.g. Sales · Client) seeds that client's
      // booking row; a legacy client-linked row with no client can't be
      // attributed, so skip it.
      if (clientLinkedSet.has(e.projectType) && !e.clientId) continue;
      const key = rowKeyOf(e.projectType, e.projectId, e.clientId);
      m.set(key, (m.get(key) || 0) + round1((e.percentage / 100) * weeklyHours / WORKDAY_LABELS.length));
    }
    return m;
  }, [planEntries, weeklyHours, clientLinkedSet]);

  const bookingByCell = React.useMemo(() => {
    const m = new Map();
    for (const b of bookings) m.set(`${b.rowKey}|${b.workDate}`, b.hours);
    return m;
  }, [bookings]);

  const decorate = React.useCallback((base) => {
    const { itemType, projectId, clientId } = base;
    const key = rowKeyOf(itemType, projectId, clientId);
    if (itemType === 'client-project') {
      const p = projectsById.get(projectId);
      const client = p ? clientsById.get(p.clientId) : null;
      return { key, itemType, projectId, label: p?.name || 'Client project', sublabel: client?.name || null, logoUrl: client?.logoUrl || null, projectHandle: projectId };
    }
    if (clientLinkedSet.has(itemType)) {
      const client = clientsById.get(clientId);
      return { key, itemType, clientId, label: client ? `${itemLabel(itemType)} · ${client.name}` : itemLabel(itemType), sublabel: null, logoUrl: client?.logoUrl || null };
    }
    return { key, itemType, label: itemLabel(itemType), sublabel: null, logoUrl: null };
  }, [projectsById, clientsById, clientLinkedSet, itemLabel]);

  const rows = React.useMemo(() => {
    const seen = new Set(); const out = [];
    const push = base => { const r = decorate(base); if (!seen.has(r.key)) { seen.add(r.key); out.push(r); } };
    // A client-linked plan row (Sales · Client) seeds its booking row;
    // skip a legacy client-linked row with no client to attribute it to.
    for (const e of planEntries) { if (clientLinkedSet.has(e.projectType) && !e.clientId) continue; push({ itemType: e.projectType, projectId: e.projectId, clientId: e.clientId }); }
    for (const b of bookings) push({ itemType: b.itemType, projectId: b.projectId, clientId: b.clientId });
    for (const r of extraRows) push(r);
    return out.sort((a, b) => rankOf(a.itemType) - rankOf(b.itemType) || a.label.localeCompare(b.label));
  }, [planEntries, bookings, extraRows, isoWeek, decorate, clientLinkedSet, rankOf]);

  // Display value of a cell: booked actual if present, else plan basis.
  const isBooked = (key, date) => bookingByCell.has(`${key}|${date}`);
  const displayVal = (key, date) => {
    const b = bookingByCell.get(`${key}|${date}`);
    if (b != null) return b;
    return round1(basisByKey.get(key) || 0);
  };
  const dayTotal = date => rows.reduce((s, r) => s + displayVal(r.key, date), 0);
  const freeFor  = date => Math.max(0, round1(maxHours - dayTotal(date)));

  // Persist one cell. hours=0 removes the booking (reverts to plan basis).
  const applyLocal = (row, date, v) => {
    setBookings(prev => {
      const idx = prev.findIndex(b => b.rowKey === row.key && b.workDate === date);
      if (v === 0) return idx >= 0 ? prev.filter((_, i) => i !== idx) : prev;
      const rec = { rowKey: row.key, workDate: date, isoWeek, itemType: row.itemType, projectId: row.projectId || null, clientId: row.clientId || null, hours: v };
      if (idx >= 0) { const n = [...prev]; n[idx] = { ...n[idx], ...rec }; return n; }
      return [...prev, rec];
    });
  };
  const saveCell = async (row, date, rawValue) => {
    let v = round1(rawValue);
    if (!Number.isFinite(v) || v < 0) v = 0;
    v = Math.min(v, maxHours);
    // Clamp to remaining hours on that day (other rows' displayed values).
    const other = rows.reduce((s, r) => r.key === row.key ? s : s + displayVal(r.key, date), 0);
    v = Math.max(0, Math.min(v, round1(maxHours - other)));
    const cur = bookingByCell.get(`${row.key}|${date}`);
    if ((cur == null && v === 0) || (cur != null && round1(cur) === v)) return;
    applyLocal(row, date, v);              // optimistic
    setStatus('saving'); setError(null);
    try {
      await upsertBooking({ userId, workDate: date, itemType: row.itemType, projectId: row.projectId || null, clientId: row.clientId || null, hours: v });
      setStatus('saved');
    } catch (err) { setError(err.message || String(err)); setStatus(null); reload(); }
  };

  // Commit inline edit.
  const commitEdit = () => {
    if (!edit) return;
    const row = rows.find(r => r.key === edit.rowKey);
    const d = edit; setEdit(null);
    if (row) saveCell(row, d.date, draft === '' ? 0 : Number(draft));
  };

  // Drag-to-fill: copy the source value across the dragged day range.
  React.useEffect(() => {
    if (!drag) return;
    const onUp = () => {
      const d = dragRef.current; setDrag(null);
      if (!d) return;
      const lo = Math.min(d.startIdx, d.endIdx), hi = Math.max(d.startIdx, d.endIdx);
      const row = rows.find(r => r.key === d.rowKey);
      if (!row) return;
      if (!d.moved) return;   // a plain click opens the editor via the cell's onClick
      (async () => { for (let i = lo; i <= hi; i++) await saveCell(row, days[i].date, d.value); })();
    };
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag, rows, days]);

  const applyPlan = async () => {
    setStatus('saving'); setError(null);
    try {
      for (const r of rows) for (const d of days) {
        if (!isBooked(r.key, d.date)) {
          const v = round1(basisByKey.get(r.key) || 0);
          if (v > 0) { applyLocal(r, d.date, v); await upsertBooking({ userId, workDate: d.date, itemType: r.itemType, projectId: r.projectId || null, clientId: r.clientId || null, hours: v }); }
        }
      }
      setStatus('saved');
    } catch (err) { setError(err.message || String(err)); setStatus(null); reload(); }
  };

  const addRow = base => { const r = decorate(base); if (!rows.some(x => x.key === r.key)) setExtraRows(p => [...p, base]); setAddOpen(false); };

  const hasUnbookedPlan = rows.some(r => days.some(d => !isBooked(r.key, d.date) && (basisByKey.get(r.key) || 0) > 0));
  const thisWeek = currentIsoWeek() === isoWeek;

  return (
    <div>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, margin:'12px 0 8px', flexWrap:'wrap'}}>
        <div style={{display:'flex', alignItems:'center', gap:8}}>
          <button onClick={() => setIsoWeek(isoWeekString(addDays(weekStart, -7)))} style={navBtn} title="Previous week">←</button>
          <div style={{fontSize:12.5, fontWeight:600}}>{isoWeek}{thisWeek && <span style={{color:'var(--orange)', fontWeight:500}}> · this week</span>}</div>
          <button onClick={() => setIsoWeek(isoWeekString(addDays(weekStart, 7)))} style={navBtn} title="Next week">→</button>
          {!thisWeek && <button onClick={() => setIsoWeek(currentIsoWeek())} style={ghostBtn}>This week</button>}
        </div>
        {editable && (
          <div style={{display:'flex', alignItems:'center', gap:8}}>
            {status === 'saving' && <span style={{fontSize:11, color:'var(--fg-muted)'}}>Saving…</span>}
            {status === 'saved' && <span style={{fontSize:11, color:'var(--support-green, #3CA88A)'}}>Saved</span>}
            {hasUnbookedPlan && <button onClick={applyPlan} style={ghostBtn} title="Book the plan basis for every day this week">Apply plan</button>}
          </div>
        )}
      </div>

      <div style={{fontSize:11.5, color:'var(--fg-muted)', marginBottom:8, lineHeight:1.5}}>
        Faint values are your plan ({weeklyHours}h/week); click a cell to type the hours you actually worked, or drag to copy across days. Max {maxHours}h/day.
      </div>

      {error && <div style={errStyle}>{error}</div>}

      <div style={{overflowX:'auto'}}>
        <div style={gridStyle}>
          <div style={{...headCell, ...STICKY_COL_HEAD, textAlign:'left', paddingLeft:12}}>Item</div>
          {days.map(d => (
            <div key={d.date} style={{...headCell, color: d.isToday ? 'var(--orange)' : undefined}}>
              {d.label}<div style={{fontSize:10, fontWeight:400, color:'var(--fg-muted)'}}>{d.dayNum}</div>
            </div>
          ))}
        </div>

        {rows.length === 0 && (
          <div style={{padding:18, textAlign:'center', color:'var(--fg-muted)', fontSize:12.5}}>
            No staffing plan for this week. {editable ? 'Use “+ Add line” to start booking.' : 'Nothing booked.'}
          </div>
        )}

        {rows.map(r => {
          const dragActive = drag && drag.rowKey === r.key;
          const lo = dragActive ? Math.min(drag.startIdx, drag.endIdx) : -1;
          const hi = dragActive ? Math.max(drag.startIdx, drag.endIdx) : -1;
          return (
            <div key={r.key} style={{...gridStyle, borderTop:'1px solid var(--border)'}}>
              <div style={{...STICKY_COL, display:'flex', alignItems:'center', gap:8, padding:'6px 10px 6px 12px', minWidth:0}}>
                {r.logoUrl
                  ? <img src={r.logoUrl} alt="" style={{width:20, height:20, objectFit:'contain', borderRadius:3, flexShrink:0, background:'var(--bg-card)'}}/>
                  : <span style={{width:10, height:10, borderRadius:3, flexShrink:0, background: typeColor(r.itemType)}}/>}
                <div style={{minWidth:0}}>
                  <div style={{fontSize:12.5, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                    {r.projectHandle ? <EntityLink type="project" handle={r.projectHandle} muted>{r.label}</EntityLink> : r.label}
                  </div>
                  {r.sublabel && <div style={{fontSize:10.5, color:'var(--fg-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{r.sublabel}</div>}
                </div>
              </div>
              {days.map((d, di) => {
                const editing = edit && edit.rowKey === r.key && edit.date === d.date;
                const booked = isBooked(r.key, d.date);
                const val = displayVal(r.key, d.date);
                const painting = dragActive && di >= lo && di <= hi;
                if (editing) {
                  return (
                    <div key={d.date} style={{...bodyCell, background: d.isToday ? 'rgba(255,100,40,0.06)' : undefined}}>
                      <input autoFocus type="number" min={0} max={maxHours} step={0.5}
                        value={draft} onChange={e => setDraft(e.target.value)}
                        onBlur={commitEdit}
                        onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEdit(null); }}
                        style={hourInput}/>
                    </div>
                  );
                }
                return (
                  <div key={d.date}
                    onMouseDown={editable ? () => { setDrag({ rowKey: r.key, startIdx: di, endIdx: di, value: val, moved: false }); } : undefined}
                    onMouseEnter={() => { if (dragRef.current && dragRef.current.rowKey === r.key) setDrag(s => s ? { ...s, endIdx: di, moved: s.moved || di !== s.startIdx } : s); }}
                    onClick={editable ? () => { setEdit({ rowKey: r.key, date: d.date }); setDraft(val ? String(round1(val)) : ''); } : undefined}
                    title={editable ? (val > 0 ? 'Click to edit · drag to copy across days' : 'Click to add hours') : undefined}
                    style={{
                      ...bodyCell,
                      cursor: editable ? (val > 0 ? 'grab' : 'pointer') : 'default',
                      background: painting ? 'rgba(255,100,40,0.18)' : (d.isToday ? 'rgba(255,100,40,0.06)' : undefined),
                      boxShadow: painting ? 'inset 0 0 0 1px var(--orange)' : undefined,
                      color: booked ? 'var(--fg)' : 'var(--fg-muted)',
                      fontWeight: booked ? 600 : 400,
                      fontStyle: booked ? 'normal' : 'italic',
                      opacity: (!booked && val === 0) ? 0.5 : 1,
                    }}>
                    {val ? `${round1(val)}h` : (editable ? '·' : '')}
                  </div>
                );
              })}
            </div>
          );
        })}

        <div style={{...gridStyle, borderTop:'1px solid var(--border)', background:'transparent'}}>
          <div style={{...STICKY_COL, padding:'6px 10px 6px 12px', fontSize:12, color:'var(--fg-muted)', fontStyle:'italic'}}>Free capacity</div>
          {days.map(d => <div key={d.date} style={{...bodyCell, color:'var(--fg-muted)'}}>{freeFor(d.date)}h</div>)}
        </div>
        <div style={{...gridStyle, borderTop:'1px solid var(--border)', background:'transparent', fontWeight:600}}>
          <div style={{...STICKY_COL, padding:'6px 10px 6px 12px', fontSize:12}}>Total</div>
          {days.map(d => {
            const t = round1(dayTotal(d.date)); const over = t > maxHours + 1e-9;
            return <div key={d.date} style={{...bodyCell, color: over ? 'var(--support-red, #C25450)' : undefined}}>{t}h{over ? ' !' : ''}</div>;
          })}
        </div>
      </div>

      {editable && (
        <div style={{marginTop:10}}>
          {addOpen
            ? <AddLineForm projects={projects} clients={clients} categories={categories} clientLinkedSet={clientLinkedSet}
                relevantProjectIds={relevantProjectIds} relevantClientIds={relevantClientIds}
                existingKeys={new Set(rows.map(r => r.key))} onAdd={addRow} onCancel={() => setAddOpen(false)}/>
            : <button onClick={() => setAddOpen(true)} style={ghostBtn}>+ Add line</button>}
        </div>
      )}
    </div>
  );
};

/* ── Relevance-ranked searchable select ───────────────────────────
 * Solves the "98 projects" problem: with no query it shows the
 * person's own items first; typing searches the whole catalogue. */
const SearchSelect = ({ options, value, onChange, placeholder, prioritizeIds }) => {
  const [q, setQ] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const pos = useAnchoredPosition(ref, open, { matchWidth: true, align: 'start', maxHeight: 240 });
  React.useEffect(() => {
    const onDoc = e => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const selected = options.find(o => (o.id || o._id) === value);
  const tokens = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const filtered = React.useMemo(() => {
    let list = options;
    if (tokens.length) list = options.filter(o => tokens.every(t => (o.name || '').toLowerCase().includes(t)));
    const prio = prioritizeIds || new Set();
    const score = o => (prio.has(o.id || o._id) ? 0 : 1);
    return [...list].sort((a, b) => score(a) - score(b) || (a.name || '').localeCompare(b.name || '')).slice(0, 40);
  }, [options, tokens, prioritizeIds]);
  const showHeader = !tokens.length && prioritizeIds && prioritizeIds.size > 0;

  return (
    <div ref={ref} style={{position:'relative', minWidth:200}}>
      <input
        value={open ? q : (selected?.name || '')}
        onFocus={() => { setOpen(true); setQ(''); }}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        placeholder={placeholder}
        style={selectStyle}/>
      {open && (
        <div style={{zIndex:50, overflowY:'auto', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:6, boxShadow:'0 8px 22px rgba(0,0,0,0.15)', ...(pos || HIDDEN_ANCHORED_STYLE)}}>
          {showHeader && <div style={{padding:'4px 10px', fontSize:10, fontWeight:600, color:'var(--fg-muted)', textTransform:'uppercase', letterSpacing:'0.05em'}}>Your items · type to search all</div>}
          {filtered.length === 0 && <div style={{padding:'8px 10px', fontSize:12, color:'var(--fg-muted)'}}>No matches.</div>}
          {filtered.map(o => {
            const id = o.id || o._id;
            const prio = (prioritizeIds || new Set()).has(id);
            return (
              <div key={id} onMouseDown={() => { onChange(id); setOpen(false); }}
                style={{padding:'7px 10px', fontSize:12.5, cursor:'pointer', display:'flex', alignItems:'center', gap:6, background: id === value ? 'var(--bg-alt)' : undefined}}>
                {prio && !tokens.length && <span style={{width:5, height:5, borderRadius:'50%', background:'var(--orange)', flexShrink:0}}/>}
                <span style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{o.name}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── Add-line inline form ─────────────────────────────────────── */
const AddLineForm = ({ projects, clients, categories, clientLinkedSet, relevantProjectIds, relevantClientIds, existingKeys, onAdd, onCancel }) => {
  const [type, setType] = React.useState('client-project');
  const [projectId, setProjectId] = React.useState('');
  const [clientId, setClientId] = React.useState('');

  const isClientLinked = clientLinkedSet.has(type);
  const canAdd = type === 'client-project' ? !!projectId : isClientLinked ? !!clientId : true;
  const submit = () => {
    if (!canAdd) return;
    const base = type === 'client-project' ? { itemType: type, projectId }
      : isClientLinked ? { itemType: type, clientId } : { itemType: type };
    const key = rowKeyOf(base.itemType, base.projectId, base.clientId);
    if (existingKeys.has(key)) { onCancel(); return; }
    onAdd(base);
  };

  return (
    <div style={{display:'flex', flexWrap:'wrap', gap:8, alignItems:'flex-end', padding:'12px 0'}}>
      <Labeled label="Type">
        <select value={type} onChange={e => setType(e.target.value)} style={selectStyle}>
          <option value="client-project">Client project</option>
          {categories.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
        </select>
      </Labeled>
      {type === 'client-project' && (
        <Labeled label="Project">
          <SearchSelect options={projects} value={projectId} onChange={setProjectId} placeholder="Search projects…" prioritizeIds={relevantProjectIds}/>
        </Labeled>
      )}
      {isClientLinked && (
        <Labeled label="Client">
          <SearchSelect options={clients} value={clientId} onChange={setClientId} placeholder="Search clients…" prioritizeIds={relevantClientIds}/>
        </Labeled>
      )}
      <button onClick={submit} disabled={!canAdd} style={{...primaryBtn, opacity: canAdd ? 1 : 0.55}}>Add</button>
      <button onClick={onCancel} style={ghostBtn}>Cancel</button>
    </div>
  );
};

const Labeled = ({ label, children }) => (
  <div style={{display:'flex', flexDirection:'column', gap:4}}>
    <span style={{fontSize:10.5, fontWeight:600, color:'var(--fg-muted)', textTransform:'uppercase', letterSpacing:'0.05em'}}>{label}</span>
    {children}
  </div>
);

/* ── styles ───────────────────────────────────────────────────── */
const GRID_COLS = `minmax(150px, 1.6fr) repeat(5, minmax(52px, 1fr))`;
const gridStyle = { display:'grid', gridTemplateColumns: GRID_COLS, minWidth: 150 + 5 * 52, alignItems:'stretch' };
// Freeze the first column (item / project) while the day columns scroll.
const STICKY_COL = { position: 'sticky', left: 0, zIndex: 2, background: 'var(--bg)' };
const STICKY_COL_HEAD = { position: 'sticky', left: 0, zIndex: 3, background: 'var(--bg)' };
const headCell = { padding:'8px 4px', textAlign:'center', fontSize:11.5, fontWeight:600, background:'transparent' };
const bodyCell = { display:'flex', alignItems:'center', justifyContent:'center', padding:'4px', textAlign:'center', fontSize:12.5, fontVariantNumeric:'tabular-nums', userSelect:'none', minHeight:34 };
const hourInput = { width:46, padding:'4px', textAlign:'center', border:'1px solid var(--orange)', borderRadius:6, fontSize:12.5, background:'var(--bg-card)', fontVariantNumeric:'tabular-nums' };
const navBtn = { padding:'4px 9px', borderRadius:6, fontSize:13, border:'1px solid var(--border)', background:'var(--bg-card)', cursor:'pointer' };
const ghostBtn = { padding:'6px 12px', borderRadius:6, fontSize:12, background:'transparent', border:'1px solid var(--border)', cursor:'pointer' };
const primaryBtn = { padding:'6px 14px', borderRadius:6, fontSize:12.5, fontWeight:600, background:'var(--orange)', color:'#fff', border:'none', cursor:'pointer' };
const selectStyle = { padding:'6px 8px', border:'1px solid var(--border)', borderRadius:6, fontSize:12.5, background:'var(--bg-card)', cursor:'pointer', width:'100%', boxSizing:'border-box' };
const errStyle = { color:'#A20235', fontSize:12, margin:'8px 0', padding:'8px 10px', background:'#FCE6E0', borderRadius:6 };
